// src/pages/Admin/Moderation/ModerationQueue.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, 
  Search, 
  CheckCircle, 
  EyeOff, 
  Trash2,
  AlertTriangle,
  Clock,
  User,
  MessageSquare,
  RefreshCw,
  Flag
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import commentService from '../../../services/commentService';
import type { ModerationComment, CommentModerationStats } from '../../../services/commentService';

type ModerationStatus = 'pending' | 'published' | 'hidden' | 'deleted' | 'flagged' | 'all';
type ModerationAction = 'approve' | 'hide' | 'delete';

const ModerationQueue: React.FC = () => {
  const [comments, setComments] = useState<ModerationComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState<CommentModerationStats | null>(null);
  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<ModerationStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  
  const { showSuccess, showError } = useToast();

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      
      let response;
      if (statusFilter === 'flagged') {
        // Use the flagged comments endpoint
        response = await commentService.getFlaggedComments({
          page: currentPage,
          page_size: 20
        });
      } else {
        // Use the regular moderation comments endpoint
        const params = {
          status: statusFilter, // Send the actual status, including 'all'
          search: searchTerm || undefined,
          content_type: contentTypeFilter || undefined,
          page: currentPage,
          page_size: 20
        };
        response = await commentService.getModerationComments(params);
      }
      
      setComments(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / 20));
    } catch (_error) {
      // Silent fail - error handling can be added if needed
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchTerm, contentTypeFilter]);

  const reloadStats = useCallback(async () => {
    try {
      const statsData = await commentService.getModerationStats();
      setStats(statsData);
    } catch (_error) {
      // Silent fail - error handling can be added if needed
    }
  }, []);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    // Load stats only once on component mount
    const loadStatsOnMount = async () => {
      try {
        setStatsLoading(true);
        const statsData = await commentService.getModerationStats();
        setStats(statsData);
      } catch (_error) {
        // Silent fail - error handling can be added if needed
      } finally {
        setStatsLoading(false);
      }
    };
    
    loadStatsOnMount();
  }, []); // Empty dependency array to load stats only once

  const handleSelectComment = (commentId: string) => {
    setSelectedComments(prev => 
      prev.includes(commentId) 
        ? prev.filter(id => id !== commentId)
        : [...prev, commentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedComments.length === comments.length) {
      setSelectedComments([]);
    } else {
      setSelectedComments(comments.map(comment => comment.id));
    }
  };

  const handleModerateComment = async (commentId: string, action: ModerationAction, reason?: string) => {
    try {
      await commentService.moderateComment(commentId, action, reason);
      showSuccess(`Comment ${action}d successfully`);
      loadComments();
      reloadStats();
      setSelectedComments(prev => prev.filter(id => id !== commentId));
    } catch (_error) {
      showError(`Failed to ${action} comment`);
    }
  };

  const handleBulkModerate = async (action: ModerationAction, reason?: string) => {
    if (selectedComments.length === 0) return;
    
    try {
      await commentService.bulkModerateComments({
        comment_ids: selectedComments,
        action,
        reason
      });
      showSuccess(`${selectedComments.length} comments ${action}d successfully`);
      loadComments();
      reloadStats();
      setSelectedComments([]);
    } catch (_error) {
      showError(`Failed to ${action} comments`);
    }
  };

  const handleFlagComment = async (commentId: string, reason?: string) => {
    try {
      await commentService.flagComment(commentId, reason || 'Inappropriate content');
      showSuccess('Comment flagged successfully');
      loadComments();
      reloadStats();
    } catch (_error) {
      showError('Failed to flag comment');
    }
  };

  const handleUnflagComment = async (commentId: string) => {
    try {
      await commentService.unflagComment(commentId);
      showSuccess('Comment unflagged successfully');
      loadComments();
      reloadStats();
    } catch (_error) {
      showError('Failed to unflag comment');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      PUBLISHED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Published' },
      HIDDEN: { color: 'bg-orange-100 text-orange-800', icon: EyeOff, label: 'Hidden' },
      DELETED: { color: 'bg-red-100 text-red-800', icon: Trash2, label: 'Deleted' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getRiskScoreBadge = (score: number) => {
    if (score >= 0.8) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">High Risk</span>;
    } else if (score >= 0.5) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Medium Risk</span>;
    } else {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Low Risk</span>;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600 flex-shrink-0" />
              <span className="truncate">Comment Moderation</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Review and moderate user comments across all content</p>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => {
                loadComments();
                reloadStats();
              }}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mt-6">
        {statsLoading ? (
          // Loading skeleton for stats
          <>
            {[...Array(5)].map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-3 sm:p-4">
                <div className="flex items-center">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded animate-pulse"></div>
                  <div className="ml-2 sm:ml-3 space-y-1 sm:space-y-2">
                    <div className="h-2 sm:h-3 bg-gray-200 rounded animate-pulse w-16 sm:w-20"></div>
                    <div className="h-4 sm:h-6 bg-gray-200 rounded animate-pulse w-8 sm:w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : stats ? (
          <>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center">
                <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Comments</p>
                  <p className="text-lg sm:text-2xl font-semibold text-gray-900">{(stats?.totalComments ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center">
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Pending</p>
                  <p className="text-lg sm:text-2xl font-semibold text-yellow-900">{(stats?.pendingComments ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Published</p>
                  <p className="text-lg sm:text-2xl font-semibold text-green-900">{(stats?.publishedComments ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center">
                <EyeOff className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Hidden</p>
                  <p className="text-lg sm:text-2xl font-semibold text-orange-900">{(stats?.hiddenComments ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Flagged</p>
                  <p className="text-lg sm:text-2xl font-semibold text-red-900">{(stats?.flaggedComments ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Fallback when no stats available
          <div className="bg-white rounded-lg shadow p-6 col-span-5 text-center">
            <p className="text-gray-500">Unable to load moderation statistics</p>
          </div>
        )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search comments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ModerationStatus)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
              <option value="deleted">Deleted</option>
              <option value="flagged">Flagged</option>
            </select>

            {/* Content Type Filter */}
            <select
              value={contentTypeFilter}
              onChange={(e) => setContentTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Content Types</option>
              <option value="cinema">Cinema</option>
              <option value="content">Content</option>
              <option value="podcast">Podcast</option>
              <option value="animation">Animation</option>
              <option value="live-video">Live Video</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedComments.length > 0 && (
          <div className="p-4 bg-blue-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-900">
                {selectedComments.length} comment{selectedComments.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkModerate('approve')}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approve
                </button>
                
                <button
                  onClick={() => handleBulkModerate('hide')}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700"
                >
                  <EyeOff className="w-3 h-3 mr-1" />
                  Hide
                </button>
                
                <button
                  onClick={() => handleBulkModerate('delete')}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comments List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No comments found</h3>
            <p className="text-gray-500">No comments match your current filters.</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedComments.length === comments.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comment Details
                </span>
              </div>
            </div>

            {/* Comments */}
            <div className="divide-y divide-gray-200">
              {comments.map((comment) => (
                <div key={comment.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedComments.includes(comment.id)}
                      onChange={() => handleSelectComment(comment.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                    />
                    
                    <div className="flex-1">
                      {/* Comment Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {comment.user?.firstName && comment.user?.lastName 
                                ? `${comment.user.firstName} ${comment.user.lastName}` 
                                : comment.user?.fullName || comment.user?.username || 'Anonymous'}
                            </span>
                          </div>
                          
                          {getStatusBadge(comment.status)}
                          {getRiskScoreBadge(comment.risk_score)}
                          
                          {(comment.report_count || comment.reportCount || 0) > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {comment.report_count || comment.reportCount || 0} report{(comment.report_count || comment.reportCount || 0) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {(comment.created_at || comment.createdAt) ? new Date(comment.created_at || comment.createdAt || '').toLocaleDateString() : 'Unknown date'} {(comment.created_at || comment.createdAt) ? new Date(comment.created_at || comment.createdAt || '').toLocaleTimeString() : ''}
                        </div>
                      </div>

                      {/* Comment Content */}
                      <div className="mb-3">
                        <p className="text-gray-900 leading-relaxed">{comment.text}</p>
                      </div>

                      {/* Comment Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <span>Content: {comment.content_title || comment.contentTitle || `${comment.content_type || 'Unknown'} ${comment.object_id ? comment.object_id.toString().substring(0, 8) : 'N/A'}`}</span>
                          <span>{comment.like_count || comment.likeCount || 0} likes</span>
                          <span>{comment.reply_count || comment.replyCount || 0} replies</span>
                        </div>
                      </div>

                      {/* Moderation Actions */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleModerateComment(comment.id, 'approve')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approve
                        </button>
                        
                        <button
                          onClick={() => handleModerateComment(comment.id, 'hide')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-orange-600 hover:bg-orange-700"
                        >
                          <EyeOff className="w-3 h-3 mr-1" />
                          Hide
                        </button>
                        
                        <button
                          onClick={() => handleModerateComment(comment.id, 'delete')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </button>

                        {(comment.report_count || comment.reportCount || 0) > 0 ? (
                          <button
                            onClick={() => handleUnflagComment(comment.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            <Flag className="w-3 h-3 mr-1" />
                            Unflag ({comment.report_count || comment.reportCount || 0})
                          </button>
                        ) : (
                          <button
                            onClick={() => handleFlagComment(comment.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Flag className="w-3 h-3 mr-1" />
                            Flag
                          </button>
                        )}
                      </div>

                      {/* Previous Moderation Info */}
                      {comment.moderated_at && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md text-xs text-gray-600">
                          <span>Previously moderated by {comment.moderated_by?.username} on {(comment.moderated_at || comment.moderatedAt) ? new Date(comment.moderated_at || comment.moderatedAt || '').toLocaleDateString() : 'Unknown date'}</span>
                          {comment.moderation_reason && (
                            <span className="block mt-1">Reason: {comment.moderation_reason}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} comments
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <span className="text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ModerationQueue;